"use client";

import React from "react";
import {
  Play,
  ChevronDown,
  FileText,
  FileQuestion,
  BookCheck,
} from "lucide-react";
import { useWatch } from "react-hook-form";
import { useAction } from "next-safe-action/hooks";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Paragraph } from "@/components/ui/typography";
import { Button } from "@/components/ui/button";

import { getCourseContent } from "@/fetchers/course";

import { cn, structureCourseFolder } from "@/lib/utils";

import CourseSection from "./course-section";

function CourseContentAccordion({ item, parent }) {
  const childItems = item.folder ?? item.resources ?? [];

  if (childItems.length === 0 && item.type !== "FOLDER") {
    return (
      <div className="flex items-center-safe gap-x-3 py-3 px-12">
        <Button
          variant="ghost"
          size="icon"
          className="flex items-center justify-center !bg-grayscale-input size-4 rounded-full"
        >
          {item.type === "TEXT" && (
            <FileText className="size-2 text-grayscale-label" />
          )}

          {item.type === "QUIZ" && (
            <FileQuestion className="size-2 text-grayscale-label" />
          )}

          {item.type === "TEST" && (
            <BookCheck className="size-2 text-grayscale-label" />
          )}

          {item.type === "VIDEO" && (
            <Play
              fill="currentColor"
              strokeWidth={4}
              className="size-1.5 text-grayscale-label"
            />
          )}
        </Button>
        <Paragraph variant="b4-tight">{item.name}</Paragraph>
      </div>
    );
  }

  const isSubFolder = parent?.type === "FOLDER" && item.type === "FOLDER";

  return (
    <AccordionItem
      value={`folder-${item.ID}`}
      className={cn(
        "rounded-xl overflow-hidden border-b-0",
        !isSubFolder && "!border border-grayscale-input"
      )}
    >
      <AccordionTrigger
        className={cn(
          "py-4 px-4 hover:no-underline",
          !isSubFolder && "bg-grayscale-weak"
        )}
      >
        <div className="flex gap-x-3 items-center">
          <Paragraph
            variant="b5"
            weight="sb"
            className="text-grayscale-placeholder"
          >
            {String(item.folderIndex).padStart(2, "0")}
          </Paragraph>
          <Paragraph
            variant={isSubFolder ? "b4-tight" : "b3"}
            weight={isSubFolder ? "default" : "sb"}
            className="text-grayscale-body"
          >
            {item.name}
          </Paragraph>
        </div>
      </AccordionTrigger>
      <AccordionContent
        className={cn("py-3", !parent && "border-t border-grayscale-input")}
      >
        {item?.folder?.length > 0 && (
          <Accordion type="single" className="space-y-3" collapsible>
            {item.folder.map((folder) => (
              <CourseContentAccordion
                key={folder.ID}
                item={folder}
                parent={item}
              />
            ))}
          </Accordion>
        )}

        {item?.resources?.map((childItem) => (
          <CourseContentAccordion
            key={childItem.id}
            item={childItem}
            parent={item}
          />
        ))}

        {childItems.length === 0 && (
          <div className="flex justify-center py-4">
            <Paragraph variant="b4" weight="sb">
              No Folder / Resources
            </Paragraph>
          </div>
        )}
      </AccordionContent>
    </AccordionItem>
  );
}

function CourseContent() {
  const courseId = useWatch({ name: "courseId" });
  const languageId = useWatch({ name: "languageId" });
  const courseMode = useWatch({ name: "mode" });
  const offlineCentreId = useWatch({ name: "offlineCentreId" });

  const {
    result: { data: courseContent },
    execute: fetchCourseContent,
  } = useAction(getCourseContent);

  const [haveExpanded, setHaveExpanded] = React.useState(false);

  React.useEffect(() => {
    if (courseId && languageId) {
      fetchCourseContent({
        courseId,
        languageId,
        offlineCentreId: courseMode === "offline" ? offlineCentreId : undefined,
      });
    }
  }, [courseId, languageId, courseMode, offlineCentreId, fetchCourseContent]);

  const items = React.useMemo(() => {
    if (!courseContent) return [];

    const contentType = courseContent?.metaContentType;
    const metaContent = courseContent[`metaFoldersType${contentType}`] ?? [];

    return structureCourseFolder(metaContent);
  }, [courseContent]);

  if (items.length === 0) return null;

  return (
    <CourseSection
      id="course-content"
      title="Course Content"
      cardContentClassName="space-y-4"
    >
      <Accordion
        type="single"
        className="space-y-3"
        defaultValue={`folder-${items?.[0]?.ID}`}
        collapsible
      >
        {items.slice(0, haveExpanded ? items.length : 5).map((item) => (
          <CourseContentAccordion key={item.ID} item={item} />
        ))}
      </Accordion>

      {items.length > 5 && (
        <div className="flex justify-center py-2">
          <Button
            variant="ghost"
            size="small"
            onClick={() => setHaveExpanded((prev) => !prev)}
          >
            <Paragraph variant="b4" weight="sb" className="text-inherit">
              Read {haveExpanded ? "Less" : "More"}
            </Paragraph>

            <ChevronDown
              className={cn(
                "rotate-0 transition-transform duration-300",
                haveExpanded && "-rotate-180"
              )}
            />
          </Button>
        </div>
      )}
    </CourseSection>
  );
}

export default CourseContent;
